import { useState, useEffect, useRef } from "react";
import { obtenerDatosTemporisApi } from "../../../../api/listas/temporisApi";
import { getNumerosEnCadena_2 } from "../../../../utils/funciones";

export const cleanStr = (str) =>
  String(str || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[.,;:_\-/]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const isStringMatch = (strA, strB) =>
  Boolean(strA && strB && (
    strA.startsWith(strB) ||
    strB.startsWith(strA) ||
    strA.includes(strB) ||
    strB.includes(strA)
  ));

const findByIds = (arr, unitId, cargoId) => {
  if (!unitId || !cargoId) return null;
  return arr.find(
    (opt) =>
      String(opt.value?.IdUnidadorganizativa) === unitId &&
      String(opt.value?.IdCargogenerico) === cargoId
  ) || null;
};

const findByObject = (arr, val) => {
  if (typeof val !== "object" || val === null || !val.IdUnidadorganizativa || !val.IdCargogenerico) {
    return null;
  }
  return arr.find(
    (opt) =>
      String(opt.value?.IdUnidadorganizativa) === String(val.IdUnidadorganizativa) &&
      String(opt.value?.IdCargogenerico) === String(val.IdCargogenerico)
  ) || null;
};

const findByDesc = (arr, cleanDesc) => {
  if (!cleanDesc) return null;
  const exact = arr.find((opt) => cleanStr(opt.label) === cleanDesc);
  if (exact) return exact;

  return arr.find((opt) => isStringMatch(cleanStr(opt.label), cleanDesc)) || null;
};

const findByCargoName = (arr, desc) => {
  if (!desc || !String(desc).includes("-")) return null;
  const cleanCargo = cleanStr(String(desc).split("-").slice(1).join("-"));
  if (!cleanCargo) return null;

  return arr.find((opt) => isStringMatch(cleanStr(opt.value?.Cargogenericonombre), cleanCargo)) || null;
};

const findByUnitIdOnly = (arr, targetUnitId) => {
  if (!targetUnitId) return null;
  const matchingUnits = arr.filter(
    (opt) => String(opt.value?.IdUnidadorganizativa) === targetUnitId
  );
  return matchingUnits.length === 1 ? matchingUnits[0] : null;
};

export const findTemporisOption = (arrTemporis, { val, desc, unitId, cargoId }) => {
  if (!arrTemporis || arrTemporis.length === 0) return null;

  const targetUnitId = String(unitId || (typeof val === "object" ? val?.IdUnidadorganizativa : val) || "").trim();
  const targetCargoId = String(cargoId || (typeof val === "object" ? val?.IdCargogenerico : "") || "").trim();
  const cleanDesc = cleanStr(desc);

  return (
    findByIds(arrTemporis, targetUnitId, targetCargoId) ||
    findByObject(arrTemporis, val) ||
    findByDesc(arrTemporis, cleanDesc) ||
    findByCargoName(arrTemporis, desc) ||
    findByUnitIdOnly(arrTemporis, targetUnitId) ||
    null
  );
};

export const useEditarCambioUnidad = (codSapNuevoCargo, formik, solicitud) => {
  const [arrTemporis, setArrTemporis] = useState([]);
  const [arrPlanHorario, setArrPlanHorario] = useState([]);
  const [cargandoTemporis, setCargandoTemporis] = useState(true);
  const isInitialSyncDone = useRef(false);
  const isInitialSyncPlanDone = useRef(false);

  useEffect(() => {
    let currentCodPos = "";
    if (typeof codSapNuevoCargo === "object" && codSapNuevoCargo !== null) {
      currentCodPos = codSapNuevoCargo.MOES_COD_POSICION || "";
    } else if (typeof codSapNuevoCargo === "string") {
      try {
        if (codSapNuevoCargo.trim().startsWith("{")) {
          currentCodPos = JSON.parse(codSapNuevoCargo).MOES_COD_POSICION || "";
        } else {
          currentCodPos = codSapNuevoCargo;
        }
      } catch (e) {
        console.warn("Error parseando codSapNuevoCargo en useEditarCambioUnidad:", e);
        currentCodPos = codSapNuevoCargo;
      }
    }

    const initCodPos = solicitud?.cod_sap_nuevo_cargo || "";
    const isDifferent = Boolean(currentCodPos && initCodPos && String(currentCodPos) !== String(initCodPos));
    if (isDifferent) {
      isInitialSyncDone.current = true;
      isInitialSyncPlanDone.current = true;
    }
  }, [codSapNuevoCargo, solicitud]);

  useEffect(() => {
    let targetObj = null;
    if (typeof codSapNuevoCargo === "object" && codSapNuevoCargo !== null) {
      targetObj = codSapNuevoCargo;
    } else if (typeof codSapNuevoCargo === "string") {
      try {
        if (codSapNuevoCargo.trim().startsWith("{")) {
          targetObj = JSON.parse(codSapNuevoCargo);
        }
      } catch (e) {
        console.error("Error parseando posición en useEditarCambioUnidad:", e);
      }
    }

    let numeroPlanHorario = "";
    let localCodigoErp = "";

    if (targetObj) {
      const planDesc = targetObj["MOES_DESCRIPCION_POSICION"] || targetObj["REEO_PLAN_HORARIO"] || "";
      numeroPlanHorario = getNumerosEnCadena_2(planDesc);
      localCodigoErp = targetObj["MOES_COD_SUB_DIVISION"] || targetObj["REEO_COD_SUB_DIVISION"] || "";
    }

    if (!localCodigoErp) {
      localCodigoErp =
        solicitud?.moes_cod_sub_division ||
        solicitud?.localcodigoerp ||
        solicitud?.cod_sap_sub_division ||
        solicitud?.sub_division ||
        solicitud?.persona?.subdivision ||
        solicitud?.persona?.cod_sub_division ||
        solicitud?.persona?.MOES_COD_SUB_DIVISION ||
        solicitud?.persona?.REEO_COD_SUB_DIVISION ||
        "";
    }

    if (!numeroPlanHorario) {
      const planDesc =
        solicitud?.nuevo_plan_horario ||
        solicitud?.plan_horario ||
        solicitud?.nombre_cargo_actual ||
        solicitud?.persona?.nombre_cargo ||
        "";
      numeroPlanHorario = getNumerosEnCadena_2(planDesc) || "45";
    }

    if (!localCodigoErp) {
      setArrTemporis([]);
      setArrPlanHorario([]);
      setCargandoTemporis(false);
      return;
    }

    setCargandoTemporis(true);
    let isMounted = true;

    obtenerDatosTemporisApi(localCodigoErp, numeroPlanHorario)
      .then((temporis) => {
        if (!isMounted || !temporis?.data) return;

        setArrPlanHorario(temporis.data || []);

        const arr = (temporis.unidad_organizativa_rol_temporis || []).map((el) => ({
          label: `${el.Unidadorganizativa} - ${el.Cargogenericonombre}`,
          value: el,
        }));

        setArrTemporis(arr);
      })
      .catch((err) => {
        console.error("Error en obtenerDatosTemporisApi:", err);
      })
      .finally(() => {
        if (isMounted) setCargandoTemporis(false);
      });

    return () => {
      isMounted = false;
    };
  }, [codSapNuevoCargo, solicitud]);

  // Sincronizar selección de Unidad Organizativa al cargar arrTemporis solo la primera vez
  useEffect(() => {
    const tipo = Number(solicitud?.id_tipo_solicitud);
    if ([1, 3].includes(tipo) && !formik.values.cod_sap_nuevo_cargo) {
      if (formik.values.nueva_unidad_organizativa) formik.setFieldValue("nueva_unidad_organizativa", "");
      if (formik.values.descripcion_unidad_temporis) formik.setFieldValue("descripcion_unidad_temporis", "");
      if (formik.values.nuevo_plan_horario) formik.setFieldValue("nuevo_plan_horario", "");
      isInitialSyncDone.current = true;
      return;
    }

    if (arrTemporis.length === 0 || isInitialSyncDone.current) return;

    const found = findTemporisOption(arrTemporis, {
      val: formik.values.nueva_unidad_organizativa,
      desc: formik.values.descripcion_unidad_temporis || solicitud?.descripcion_unidad_temporis,
      unitId: solicitud?.idunidadorganizativa || solicitud?.nueva_unidad_organizativa,
      cargoId: solicitud?.idcargogenerico,
    });

    if (found) {
      formik.setFieldValue("nueva_unidad_organizativa", found.value);
      if (found.label) {
        formik.setFieldValue("descripcion_unidad_temporis", found.label);
      }
    } else {
      formik.setFieldValue("nueva_unidad_organizativa", "");
      formik.setFieldValue("descripcion_unidad_temporis", "");
      formik.setFieldValue("nuevo_plan_horario", "");
    }
    isInitialSyncDone.current = true;
  }, [arrTemporis, solicitud, formik.values.cod_sap_nuevo_cargo]);

  // Sincronizar selección de Plan Horario al cargar arrPlanHorario solo la primera vez
  useEffect(() => {
    const tipo = Number(solicitud?.id_tipo_solicitud);
    const hasCargoParent = ![1, 3].includes(tipo) || Boolean(formik.values.cod_sap_nuevo_cargo);
    const hasUnidadParent = Boolean(formik.values.nueva_unidad_organizativa);

    if (!hasCargoParent || !hasUnidadParent) {
      if (formik.values.nuevo_plan_horario !== "") {
        formik.setFieldValue("nuevo_plan_horario", "");
      }
      isInitialSyncPlanDone.current = true;
      return;
    }

    if (arrPlanHorario.length === 0 || isInitialSyncPlanDone.current) return;

    const currentPlan = String(formik.values.nuevo_plan_horario || solicitud?.nuevo_plan_horario || solicitud?.idplanhorario || "").trim();
    const code = currentPlan.includes("|") ? currentPlan.split("|")[0] : currentPlan;

    let unPlan = null;
    if (code) {
      unPlan = arrPlanHorario.find(
        (el) => String(el.id) === code || String(el.Planhorariocodigoerp) === code
      );
    }

    if (!unPlan && currentPlan) {
      const cleanCurrent = cleanStr(currentPlan);
      unPlan = arrPlanHorario.find((el) => cleanStr(el.Planhorarionombre).includes(cleanCurrent) || cleanCurrent.includes(cleanStr(el.Planhorarionombre)));
    }

    if (unPlan) {
      const formatted = `${unPlan.Planhorariocodigoerp}|${unPlan.Planhorarionombre}`;
      if (formik.values.nuevo_plan_horario !== formatted) {
        formik.setFieldValue("nuevo_plan_horario", formatted);
      }
    } else if (arrPlanHorario.length === 1 && hasUnidadParent) {
      const singlePlan = arrPlanHorario[0];
      const formatted = `${singlePlan.Planhorariocodigoerp}|${singlePlan.Planhorarionombre}`;
      formik.setFieldValue("nuevo_plan_horario", formatted);
    } else {
      formik.setFieldValue("nuevo_plan_horario", "");
    }
    isInitialSyncPlanDone.current = true;
  }, [arrPlanHorario, solicitud, formik.values.cod_sap_nuevo_cargo, formik.values.nueva_unidad_organizativa]);

  return {
    arrTemporis,
    arrPlanHorario,
    cargandoTemporis,
  };
};
